/**
 * Habit Completion Flow — sequential modals for timer → value → proof.
 * A single habit can require all three. Order: timer → value → proof → submit.
 * If habit requires none, callback fires immediately (no modal).
 */

import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { Camera, Video, FileText, Play, Pause, Check, X, Plus } from 'lucide-react'
import { useMediaUpload } from '@turnifly/shared/hooks'
import type { ProofMedia } from '@turnifly/shared/api'
import type { MonthlyHabitItem } from '@turnifly/shared/types'
import { useAuthStore } from '@turnifly/shared/stores'
import { useCreatePost } from '@turnifly/shared/hooks'

// ── Types ──

type FlowStep = 'idle' | 'timer' | 'value' | 'proof' | 'feed'

interface CollectedData {
  value: number | null
  proofUrls: ProofMedia[]
  proofText: string | null
}

export interface PendingToggle {
  habit: MonthlyHabitItem
  logDate: string
}

interface HabitCompletionFlowProps {
  pending: PendingToggle | null
  onComplete: (habitId: string, logDate: string, data: CollectedData) => void
  onCancel: () => void
}

// ── Helpers ──

function needsTimer(habit: MonthlyHabitItem): boolean {
  return !!habit.time_duration && habit.time_duration > 0
}

function needsValue(habit: MonthlyHabitItem): boolean {
  return habit.value_required === true
}

function needsProof(habit: MonthlyHabitItem): boolean {
  return Array.isArray(habit.proof_types) && habit.proof_types.length > 0
}

function getFirstStep(habit: MonthlyHabitItem): FlowStep {
  if (needsTimer(habit)) return 'timer'
  if (needsValue(habit)) return 'value'
  if (needsProof(habit)) return 'proof'
  return 'idle'
}

function getNextStep(habit: MonthlyHabitItem, current: FlowStep): FlowStep {
  if (current === 'timer') {
    if (needsValue(habit)) return 'value'
    if (needsProof(habit)) return 'proof'
    return 'idle'
  }
  if (current === 'value') {
    if (needsProof(habit)) return 'proof'
    return 'idle'
  }
  if (current === 'proof') {
    return 'feed'
  }
  return 'idle'
}

/** Check if habit needs any modal before completing */
// eslint-disable-next-line react-refresh/only-export-components
export function habitNeedsFlow(habit: MonthlyHabitItem): boolean {
  return needsTimer(habit) || needsValue(habit) || needsProof(habit)
}

// ── Main Component ──

export const HabitCompletionFlow: React.FC<HabitCompletionFlowProps> = ({
  pending, onComplete, onCancel,
}) => {
  const [step, setStep] = useState<FlowStep>('idle')
  const [collected, setCollected] = useState<CollectedData>({ value: null, proofUrls: [], proofText: null })
  const { user } = useAuthStore()
  const createPost = useCreatePost()

  // Reset when pending changes
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (pending) {
      const first = getFirstStep(pending.habit)
      setStep(first)
      setCollected({ value: null, proofUrls: [], proofText: null })
    } else {
      setStep('idle')
    }
  }, [pending])
  /* eslint-enable react-hooks/set-state-in-effect */

  const advance = useCallback((partial: Partial<CollectedData>): void => {
    if (!pending) return
    const updated = { ...collected, ...partial }
    setCollected(updated)
    const next = getNextStep(pending.habit, step)
    if (next === 'idle') {
      onComplete(pending.habit.id, pending.logDate, updated)
    } else {
      setStep(next)
    }
  }, [pending, step, collected, onComplete])

  if (!pending || step === 'idle') return null

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="flow-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onCancel}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
      />
      {/* Sheet */}
      <motion.div
        key="flow-sheet"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--color-bg)] rounded-t-[24px] border-t border-white/[0.08] max-w-md mx-auto"
      >
        <div className="w-full flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-white/20 rounded-full" />
        </div>
        <div className="px-5 pb-safe mb-5">
          {step === 'timer' && (
            <TimerStep
              durationMinutes={pending.habit.time_duration!}
              habitName={pending.habit.name}
              onDone={() => advance({})}
              onCancel={onCancel}
            />
          )}
          {step === 'value' && (
            <ValueStep
              habit={pending.habit}
              onSubmit={(value) => advance({ value })}
              onCancel={onCancel}
            />
          )}
          {step === 'proof' && (
            <ProofStep
              proofTypes={pending.habit.proof_types}
              onSubmit={(proofUrls, proofText) => advance({ proofUrls, proofText })}
              onCancel={onCancel}
            />
          )}
          {step === 'feed' && (
            <FeedPromptStep
              proofUrls={collected.proofUrls}
              proofText={collected.proofText}
              onSubmit={(shouldPost) => {
                if (shouldPost && pending) {
                  // Sort media: images first, videos last (carousel format)
                  const images = collected.proofUrls.filter((p) => p.type === 'image')
                  const videos = collected.proofUrls.filter((p) => p.type === 'video')
                  const sortedMedia = [...images, ...videos]
                  const mediaUrls = sortedMedia.map((p) => p.url)
                  const hasMedia = mediaUrls.length > 0
                  const hasVideo = videos.length > 0
                  const mediaType = hasMedia ? (hasVideo ? 'video' : 'image') : null

                  // Content: habit icon + name, then proof text as caption
                  const icon = pending.habit.icon ?? ''
                  const textParts = [`${icon} ${pending.habit.name}`.trim()]
                  if (collected.proofText) textParts.push(`\n\n${collected.proofText}`)

                  createPost.mutate({
                    userId: user?.id ?? '',
                    content: textParts.join(''),
                    mediaUrls: hasMedia ? mediaUrls : undefined,
                    mediaType,
                  })
                }
                advance({})
              }}
            />
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

// ── Timer Step ──

interface TimerStepProps {
  durationMinutes: number // minutes from DB (time_duration field)
  habitName: string
  onDone: () => void
  onCancel: () => void
}

const TimerStep: React.FC<TimerStepProps> = ({ durationMinutes, habitName, onDone, onCancel }) => {
  const { t } = useTranslation()
  const totalSeconds = durationMinutes * 60
  const [remaining, setRemaining] = useState(totalSeconds)
  const [running, setRunning] = useState(false)
  const isDone = remaining <= 0

  useEffect(() => {
    if (!running || remaining <= 0) return
    const id = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) { setRunning(false); return 0 }
        return prev - 1
      })
    }, 1000)
    return (): void => clearInterval(id)
  }, [running]) // eslint-disable-line react-hooks/exhaustive-deps

  const mins = Math.floor(remaining / 60)
  const secs = remaining % 60
  const progress = totalSeconds > 0 ? (totalSeconds - remaining) / totalSeconds : 1

  return (
    <div className="flex flex-col items-center gap-4 py-2">
      <span className="text-sm font-semibold text-text-hint">{habitName}</span>

      {/* Circular timer */}
      <div className="relative w-32 h-32">
        <svg width={128} height={128} viewBox="0 0 128 128" className="-rotate-90">
          <circle cx="64" cy="64" r="56" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
          <circle
            cx="64" cy="64" r="56" fill="none"
            stroke={isDone ? 'var(--color-success)' : 'var(--color-primary)'}
            strokeWidth="6" strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 56}
            strokeDashoffset={2 * Math.PI * 56 * (1 - progress)}
            className="transition-all duration-1000 ease-linear"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-3xl font-display font-bold text-text tabular-nums">
            {isDone ? '✓' : `${mins}:${String(secs).padStart(2, '0')}`}
          </span>
        </div>
      </div>

      <span className="text-xs text-text-hint">
        {isDone ? t('home.completion.timerDone') : running ? t('home.completion.timerRunning') : t('home.completion.timerTitle')}
      </span>

      <div className="flex gap-3 w-full">
        <button onClick={onCancel} className="flex-1 h-11 rounded-xl border border-white/[0.08] text-text-hint text-sm font-medium">
          <X size={16} className="inline mr-1" />{t('common.cancel')}
        </button>
        {isDone ? (
          <button onClick={onDone} className="flex-1 h-11 rounded-xl bg-success text-white font-semibold text-sm">
            <Check size={16} className="inline mr-1" />{t('home.completion.confirm')}
          </button>
        ) : (
          <button
            onClick={() => setRunning(!running)}
            className="flex-1 h-11 rounded-xl bg-primary text-white font-semibold text-sm"
          >
            {running ? (
              <><Pause size={16} className="inline mr-1" />{t('home.completion.timerPause')}</>
            ) : (
              <><Play size={16} className="inline mr-1" />{remaining === totalSeconds ? t('home.completion.timerStart') : t('home.completion.timerResume')}</>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Value Step ──

interface ValueStepProps {
  habit: MonthlyHabitItem
  onSubmit: (value: number) => void
  onCancel: () => void
}

const ValueStep: React.FC<ValueStepProps> = ({ habit, onSubmit, onCancel }) => {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const numValue = parseFloat(input)
  const isValid = !isNaN(numValue) && numValue > 0

  const handleSubmit = (): void => {
    if (isValid) onSubmit(numValue)
  }

  return (
    <div className="flex flex-col gap-4 py-2">
      <div className="text-center">
        <span className="text-lg">{habit.icon}</span>
        <h3 className="text-base font-display font-bold text-text mt-1">{t('home.completion.enterValue')}</h3>
        {habit.value_target && habit.value_unit && (
          <p className="text-xs text-text-hint mt-1">
            {t('home.completion.target', { target: habit.value_target, unit: habit.value_unit })}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="number"
          inputMode="decimal"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && isValid) handleSubmit() }}
          placeholder={t('home.completion.valuePlaceholder')}
          autoFocus
          className="flex-1 h-12 px-4 rounded-xl bg-white/[0.04] border border-white/[0.08] text-text text-lg font-semibold text-center focus:border-primary/50 focus:outline-none transition-colors"
        />
        {habit.value_unit && (
          <span className="text-sm text-text-hint shrink-0">{habit.value_unit}</span>
        )}
      </div>

      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 h-11 rounded-xl border border-white/[0.08] text-text-hint text-sm font-medium">
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={!isValid}
          className="flex-1 h-11 rounded-xl bg-primary text-white font-semibold text-sm disabled:opacity-40"
        >
          {t('home.completion.confirm')}
        </button>
      </div>
    </div>
  )
}

// ── Proof Step ──

const MAX_IMAGES = 3
const MAX_VIDEO_SIZE_MB = 50
const MAX_VIDEO_DURATION_S = 30
const MAX_PROOF_TEXT = 400

interface ProofStepProps {
  proofTypes: string[]
  onSubmit: (proofUrls: ProofMedia[], proofText: string | null) => void
  onCancel: () => void
}

const ProofStep: React.FC<ProofStepProps> = ({ proofTypes, onSubmit, onCancel }) => {
  const { t } = useTranslation()
  const [images, setImages] = useState<ProofMedia[]>([])
  const [video, setVideo] = useState<ProofMedia | null>(null)
  const [textInput, setTextInput] = useState('')
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadingType, setUploadingType] = useState<string | null>(null)
  const { upload } = useMediaUpload()
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const hasImage = proofTypes.includes('image') || proofTypes.includes('photo')
  const hasVideo = proofTypes.includes('video')
  const hasText = proofTypes.includes('text')

  const hasAnyProof = images.length > 0 || video !== null || textInput.trim().length > 0

  const handleImagePick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = e.target.files
    if (!files) return
    const remaining = MAX_IMAGES - images.length
    const toUpload = Array.from(files).slice(0, remaining)
    if (toUpload.length === 0) return

    setUploadingType('image')
    setUploadError(null)
    for (const file of toUpload) {
      try {
        const result = await upload(file, 'proof')
        if (result?.url) {
          setImages((prev) => [...prev, { url: result.url, type: 'image' }])
        }
      } catch {
        setUploadError(t('home.completion.uploadFailed'))
      }
    }
    setUploadingType(null)
    if (imageInputRef.current) imageInputRef.current.value = ''
  }, [images.length, upload, t])

  const handleVideoPick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    if (!file) return

    // Size check
    if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      setUploadError(t('home.completion.videoTooLarge', { max: MAX_VIDEO_SIZE_MB }))
      return
    }

    // Duration check
    const durationOk = await checkVideoDuration(file, MAX_VIDEO_DURATION_S)
    if (!durationOk) {
      setUploadError(t('home.completion.videoTooLong', { max: MAX_VIDEO_DURATION_S }))
      return
    }

    setUploadingType('video')
    setUploadError(null)
    try {
      const result = await upload(file, 'proof')
      if (result?.url) {
        setVideo({ url: result.url, type: 'video' })
      }
    } catch {
      setUploadError(t('home.completion.uploadFailed'))
    }
    setUploadingType(null)
    if (videoInputRef.current) videoInputRef.current.value = ''
  }, [upload, t])

  const removeImage = useCallback((index: number): void => {
    setImages((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSubmit = (): void => {
    const allMedia: ProofMedia[] = [...images]
    if (video) allMedia.push(video)
    const text = textInput.trim() || null
    onSubmit(allMedia, text)
  }

  return (
    <div className="flex flex-col gap-5 py-2">
      <h3 className="text-base font-display font-bold text-text text-center">
        {t('home.completion.uploadProof')}
      </h3>

      {uploadError && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs text-danger text-center bg-danger/10 rounded-xl py-2.5 px-3"
        >
          {uploadError}
        </motion.div>
      )}

      {/* ── Image Section ── */}
      {hasImage && (
        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-medium text-text-hint/60 uppercase tracking-wider mb-2.5">
            <Camera size={12} />
            {t('home.completion.proofPhoto')}
            <span className="text-text-hint/30 ml-auto normal-case tracking-normal">
              {images.length}/{MAX_IMAGES}
            </span>
          </label>

          <div className="flex gap-2">
            {images.map((img, i) => (
              <motion.div
                key={img.url}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative w-[72px] h-[72px] rounded-xl overflow-hidden ring-1 ring-white/[0.06]"
              >
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/70 text-white/90 backdrop-blur-sm"
                >
                  <X size={10} />
                </button>
              </motion.div>
            ))}

            {images.length < MAX_IMAGES && (
              <>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagePick}
                  className="hidden"
                />
                <button
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingType === 'image'}
                  className="w-[72px] h-[72px] rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] flex flex-col items-center justify-center gap-1 text-text-hint/40 hover:border-primary/30 hover:text-primary/50 transition-colors disabled:opacity-40"
                >
                  {uploadingType === 'image' ? (
                    <div className="w-5 h-5 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                  ) : (
                    <>
                      <Plus size={18} strokeWidth={1.5} />
                      <span className="text-[9px]">{t('home.completion.addPhoto')}</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Video Section ── */}
      {hasVideo && (
        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-medium text-text-hint/60 uppercase tracking-wider mb-2.5">
            <Video size={12} />
            {t('home.completion.proofVideo')}
            <span className="text-text-hint/30 ml-auto normal-case tracking-normal">
              {t('home.completion.videoLimit', { seconds: MAX_VIDEO_DURATION_S })}
            </span>
          </label>

          {video ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative w-full h-36 rounded-xl overflow-hidden ring-1 ring-white/[0.06]"
            >
              <video
                src={video.url}
                className="w-full h-full object-cover"
                controls={false}
                muted
                playsInline
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <button
                onClick={() => setVideo(null)}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/60 text-white/90 backdrop-blur-sm"
              >
                <X size={12} />
              </button>
              <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/50 backdrop-blur-sm">
                <Video size={10} className="text-white/70" />
                <span className="text-[10px] text-white/70">{t('home.completion.videoReady')}</span>
              </div>
            </motion.div>
          ) : (
            <>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoPick}
                className="hidden"
              />
              <button
                onClick={() => videoInputRef.current?.click()}
                disabled={uploadingType === 'video'}
                className="w-full h-20 rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] flex items-center justify-center gap-2.5 text-text-hint/40 hover:border-primary/30 hover:text-primary/50 transition-colors disabled:opacity-40"
              >
                {uploadingType === 'video' ? (
                  <>
                    <div className="w-5 h-5 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                    <span className="text-sm text-text-hint/50">{t('common.uploading')}</span>
                  </>
                ) : (
                  <>
                    <Video size={20} strokeWidth={1.5} />
                    <span className="text-sm">{t('home.completion.addVideo')}</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Text Section ── */}
      {hasText && (
        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-medium text-text-hint/60 uppercase tracking-wider mb-2.5">
            <FileText size={12} />
            {t('home.completion.proofText')}
          </label>

          <div className="relative">
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value.slice(0, MAX_PROOF_TEXT))}
              placeholder={t('home.completion.proofTextPlaceholder')}
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-text text-sm resize-none focus:border-primary/30 focus:outline-none transition-colors leading-relaxed"
            />
            <span className={`absolute bottom-2.5 right-3 text-[10px] tabular-nums ${
              textInput.length >= MAX_PROOF_TEXT ? 'text-danger' : 'text-text-hint/25'
            }`}>
              {textInput.length}/{MAX_PROOF_TEXT}
            </span>
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={onCancel}
          className="flex-1 h-11 rounded-xl border border-white/[0.08] text-text-hint text-sm font-medium"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={!hasAnyProof || !!uploadingType}
          className="flex-1 h-11 rounded-xl bg-primary text-white font-semibold text-sm disabled:opacity-40 flex items-center justify-center gap-1.5 transition-opacity"
        >
          <Check size={16} />
          {t('home.completion.confirm')}
        </button>
      </div>
    </div>
  )
}

/** Checks if a video file duration is within the allowed limit */
function checkVideoDuration(file: File, maxSeconds: number): Promise<boolean> {
  return new Promise((resolve) => {
    const videoEl = document.createElement('video')
    videoEl.preload = 'metadata'
    videoEl.onloadedmetadata = (): void => {
      URL.revokeObjectURL(videoEl.src)
      resolve(videoEl.duration <= maxSeconds)
    }
    videoEl.onerror = (): void => {
      URL.revokeObjectURL(videoEl.src)
      resolve(true) // allow on error — server can re-validate
    }
    videoEl.src = URL.createObjectURL(file)
  })
}

// ── Feed Prompt Step ──

interface FeedPromptStepProps {
  proofUrls: ProofMedia[]
  proofText: string | null
  onSubmit: (post: boolean) => void
}

const FeedPromptStep: React.FC<FeedPromptStepProps> = ({ proofUrls, proofText, onSubmit }) => {
  const { t } = useTranslation()
  const imageProofs = proofUrls.filter((p) => p.type === 'image')
  const videoProof = proofUrls.find((p) => p.type === 'video')
  const hasContent = imageProofs.length > 0 || !!videoProof || !!proofText

  return (
    <div className="flex flex-col gap-4 py-2">
      <h3 className="text-base font-display font-bold text-text text-center">
        {t('home.completion.postToFeed')}
      </h3>

      {hasContent && (
        <div className="space-y-2.5 my-1">
          {/* Text preview */}
          {proofText && (
            <div className="w-full max-h-24 overflow-y-auto p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-[13px] text-text-hint/70 italic leading-relaxed">
              &ldquo;{proofText}&rdquo;
            </div>
          )}

          {/* Media previews */}
          <div className="flex gap-2 overflow-x-auto">
            {imageProofs.map((img) => (
              <img
                key={img.url}
                src={img.url}
                alt=""
                className="w-20 h-20 object-cover rounded-xl ring-1 ring-white/[0.06] shrink-0"
              />
            ))}
            {videoProof && (
              <div className="relative w-20 h-20 rounded-xl overflow-hidden ring-1 ring-white/[0.06] shrink-0">
                <video src={videoProof.url} className="w-full h-full object-cover" muted playsInline />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <Play size={16} className="text-white/80" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <p className="text-[13px] text-center text-text-hint/50 leading-relaxed">
        {t('home.completion.postToFeedDesc')}
      </p>

      <div className="flex gap-3 mt-1">
        <button
          onClick={() => onSubmit(false)}
          className="flex-1 h-11 rounded-xl border border-white/[0.08] text-text-hint text-sm font-medium"
        >
          {t('common.no')}
        </button>
        <button
          onClick={() => onSubmit(true)}
          className="flex-1 h-11 rounded-xl bg-primary text-white font-semibold text-sm"
        >
          {t('common.yes')}
        </button>
      </div>
    </div>
  )
}
